// The wire types of sculpt-api's account and admin routes (sculpt-api/README.md "Admin", PLAN-accounts.md §9,
// PLAN-offers.md §8 for billing).
// Decimals travel as strings ("20.00"); dates as ISO strings.

export type Decimal = string;

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  refresh_token?: string | null;
  is_active?: boolean;
}

export interface OrganizationBrief {
  id: number;
  name: string;
  kind: string;
  status: string;
  role: string;
  /** The key of the current subscription's offer ("" without one). */
  plan_name: string;
  /** Since when its owner keeps it out of the review's sample; null: in it. */
  review_sample_opt_out_at?: string | null;
}

export interface Identity {
  provider: string;
  email: string | null;
  tenant_id: string | null;
  display_name: string | null;
  last_used_at: string | null;
}

/** GET /user */
export interface Me {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  locale: string;
  email_verified: boolean;
  sso_only: boolean;
  organization: OrganizationBrief | null;
}

export interface UserAdmin {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  locale: string;
  email_verified: boolean;
  sso_only: boolean;
  created_at: string;
  last_connection: string | null;
  organization: OrganizationBrief | null;
  identities: Identity[];
  /** What the client's calls are worth at client price (EUR); `cost_usd` is what the provider charged us. */
  billed_eur: number;
  cost_usd: number;
  request_count: number;
  /** Since when the person objects to the review's sample (staff record it on their request); null: no objection. */
  review_sample_objection_at: string | null;
  /** How the person heard of Sculpt, the optional question of their first connection; null: no answer. */
  discovery_source: DiscoverySource | null;
}

/** The answers to that question (sculpt-api `users/schemas.py`), and `invitation`, stored for an account an invitation
 *  brought, which is not asked. */
export const DISCOVERY_SOURCES = ['linkedin', 'google', 'youtube', 'word_of_mouth', 'other', 'invitation'] as const;
export type DiscoverySource = (typeof DISCOVERY_SOURCES)[number];

export interface UserAdminPatch {
  first_name?: string;
  last_name?: string;
  locale?: string;
  is_active?: boolean;
  is_staff?: boolean;
  email_verified?: boolean;
}

export interface UserCreate {
  email: string;
  first_name: string;
  last_name: string;
  locale: string;
  organization_id: number | null;
  role: 'admin' | 'member';
  is_staff: boolean;
  send_reset: boolean;
}

export interface UsersQuery {
  q?: string;
  organization_id?: number;
  is_staff?: boolean;
  verified?: boolean;
  discovery_source?: string;
  page?: number;
  page_size?: number;
}

export type OrgKind = 'personal' | 'team';
export type OrgStatus = 'active' | 'suspended' | 'archived';
export type UsagePeriod = 'trial' | 'month';

/** Who is billed (sculpt-api PLAN-payments.md §3): Sculpt is sold to professionals only, `individual` is a sole trader. */
export type CustomerKind = 'company' | 'individual';
/** What VIES said of the VAT number; `review`: VIES could not answer, staff settle it (P9). */
export type VatStatus = 'none' | 'valid' | 'invalid' | 'review';
/** Card by default; `transfer` is staff-only (P4). */
export type PaymentMethod = 'card' | 'transfer';
/** An order's VAT: `review` holds it (no rate, not payable) until the VAT number is settled; null = no profile yet. */
export type VatScheme = 'standard' | 'reverse_charge' | 'outside_scope' | 'review';

export interface OrganizationAdmin {
  id: number;
  name: string;
  kind: OrgKind;
  status: OrgStatus;
  billing_email: string | null;
  billing_name: string | null;
  billing_address: string | null;
  vat_number: string | null;
  customer_kind: CustomerKind | null;
  billing_street: string | null;
  billing_zip: string | null;
  billing_city: string | null;
  /** ISO 3166-1 alpha-2. */
  billing_country: string | null;
  siren: string | null;
  registration_number: string | null;
  vat_status: VatStatus;
  vat_checked_at: string | null;
  /** What VIES has on record for the number, and its consultation number (proof of the check). */
  vat_check_name: string | null;
  vat_check_address: string | null;
  vat_check_ref: string | null;
  /** Staff who settled a number VIES could not answer for; null with `vat_decided_at` set: VIES answered later. */
  vat_decided_by: number | null;
  vat_decided_at: string | null;
  payment_method: PaymentMethod;
  /** What it is priced and invoiced in (sculpt-api PLAN-multi-currency.md); staff change it until it has paid. */
  currency: string;
  /** Since when its owner keeps it out of the review's sample (only the owner sets it, from the portal); null: in it. */
  review_sample_opt_out_at: string | null;
  /** The organization as a Mollie customer, and the card it last paid with, saved as a mandate (PLAN-payments.md M3). */
  mollie_customer_id: string | null;
  mollie_mandate_id: string | null;
  card_label: string | null;
  card_expires_on: string | null;
  /** Everything an invoice needs is there (kind, name, address, country, SIREN of a French customer). */
  billing_complete: boolean;
  notes: string;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  member_count: number;
  subscription: SubscriptionBrief | null;
  /** What the client's calls are worth at client price (EUR), all time; `cost_usd` is what the provider charged us. */
  billed_eur: number;
  cost_usd: number;
}

export interface OrganizationAdminPatch {
  name?: string;
  kind?: OrgKind;
  status?: OrgStatus;
  billing_email?: string | null;
  billing_name?: string | null;
  billing_address?: string | null;
  vat_number?: string | null;
  customer_kind?: CustomerKind | null;
  billing_street?: string | null;
  billing_zip?: string | null;
  billing_city?: string | null;
  billing_country?: string | null;
  siren?: string | null;
  registration_number?: string | null;
  payment_method?: PaymentMethod;
  /** Another currency: 409 `currency_locked` once it paid an order, `currency_not_supported` without a rate. */
  currency?: string;
  notes?: string;
}

export interface OrganizationCreate {
  name: string;
  billing_email: string | null;
  notes: string;
  first_admin_email: string | null;
}

export interface OrganizationsQuery {
  q?: string;
  kind?: string;
  status?: string;
  offer?: string;
  page?: number;
  page_size?: number;
}

export interface Member {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  email_verified: boolean;
  is_active: boolean;
  joined_at: string;
  last_connection: string | null;
  cap_eur: Decimal | null;
}

export interface Claim {
  id: number;
  kind: 'domain' | 'tenant';
  value: string;
  auto_join: boolean;
  verified_at: string | null;
  created_at: string;
}

export interface Invitation {
  id: number;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  invited_by: number | null;
}

export interface MemberUsage {
  user_id: number;
  email: string;
  name: string;
  /** In the summary's currency (the name is historical). */
  spent_usd: number;
  request_count: number;
  cap: number | null;
}

/** The subscription's pool for the period; the `_usd` names are historical, amounts are in `currency`. */
export interface OrganizationUsage {
  spent_usd: number;
  budget_usd: number;
  remaining_usd: number;
  request_count: number;
  limit_reached: boolean;
  unlimited: boolean;
  period: UsagePeriod;
  period_start: string | null;
  period_end: string | null;
  organization_id: number | null;
  currency: string;
  reason: string | null;
  offer: string | null;
  subscription_status: string | null;
  credits: number;
  members: MemberUsage[];
}

export interface OrganizationDetail extends OrganizationAdmin {
  members: Member[];
  claims: Claim[];
  invitations: Invitation[];
  usage: OrganizationUsage | null;
  invoices: Invoice[];
  billing: BillingState | null;
  credits: CreditGrant[];
  orders: Order[];
}

// ---- offers, subscriptions, credits, orders (PLAN-offers.md) ----

/** A monthly usage level (PLAN-offers.md §15): `price` excluding VAT buys `usage` (EUR at client price). */
export interface CreditTier {
  price: Decimal;
  usage: Decimal;
}

/** An offer's terms, in its currency: a price list in another currency is another offer under the same key. */
export interface Terms {
  /** Set when the offer is created (default EUR); fixed afterwards (409 `currency_fixed`). */
  currency: string;
  price_per_seat: Decimal;
  /** Usage each seat adds to the pool per month (the trial: the whole pool); null = unlimited. */
  allowance_per_seat_eur: Decimal | null;
  /** What one member may draw from the pool per month; null = no cap. */
  user_cap_eur: Decimal | null;
  min_seats: number;
  max_seats: number | null;
  trial_days: number | null;
  features: string[];
  /** The tiers a subscriber chooses from (Solo, Team); null = no choice. */
  credit_tiers: CreditTier[] | null;
}

export interface OfferVersion extends Terms {
  id: number;
  version: number;
  period: string;
  created_at: string;
  /** Set once a subscription used it: from then on, new terms make a new version. */
  locked_at: string | null;
  subscribers: number;
}

export interface OfferAdmin {
  id: number;
  key: string;
  name: string;
  description: string;
  trial: boolean;
  public: boolean;
  active: boolean;
  sort: number;
  /** How many current subscriptions the offer may hold (Free: 50); null = no limit. */
  max_active_subscriptions: number | null;
  subscribers: number;
  /** The current version (what a new subscription takes). */
  terms: OfferVersion;
  /** Newest first. */
  versions: OfferVersion[];
}

export interface OfferCreate {
  key: string;
  name: string;
  description: string;
  public: boolean;
  active: boolean;
  sort: number;
  terms: Terms;
}

export interface OfferPatch {
  name?: string;
  description?: string;
  public?: boolean;
  active?: boolean;
  sort?: number;
  max_active_subscriptions?: number | null;
}

export interface TermsResult {
  version: OfferVersion;
  new_version: boolean;
}

export interface MigrateResult {
  /** Moved now: the date was already reached (the period is kept, the next renewal bills the new terms). */
  applied: number;
  scheduled: number;
  effective_from: string;
}

/** A pack in one currency (`price` and `credit_eur` are in it; the `_eur` name is historical); its key names it in each. */
export interface CreditPackAdmin {
  id: number;
  key: string;
  name: string;
  currency: string;
  price: Decimal;
  credit_eur: Decimal;
  validity_months: number | null;
  public: boolean;
  active: boolean;
  sort: number;
}

export type CreditPackIn = Omit<CreditPackAdmin, 'id'>;
export type CreditPackPatch = Partial<Omit<CreditPackAdmin, 'id' | 'key' | 'currency'>>;

// ---- currencies and the price table (sculpt-api PLAN-multi-currency.md §2b) ----

/** A currency as staff set it. EUR is always listed first: rate 1, on sale, `fixed`. */
export interface CurrencyOut {
  code: string;
  /** What one unit is worth in euros (USD 0.86): converts credits and usage to the euro ledger, never a price. */
  rate: Decimal;
  /** Customers may be priced in it. */
  on_sale: boolean;
  fixed: boolean;
  updated_at: string | null;
}

/** PUT /admin/currencies/{code}: created on first use; 409 `eur_fixed` for EUR. */
export interface CurrencyIn {
  rate: Decimal;
  on_sale: boolean;
}

/** An offer's money terms in one currency (its latest version); every amount is in that currency (the `_eur` names
 *  are historical). */
export interface OfferPrices {
  offer_id: number | null;
  version: number | null;
  public: boolean | null;
  active: boolean | null;
  subscribers: number;
  price_per_seat: Decimal;
  /** null = unlimited. */
  allowance_per_seat_eur: Decimal | null;
  /** null = no cap. */
  user_cap_eur: Decimal | null;
  /** Matched by rank across currencies. */
  credit_tiers: CreditTier[] | null;
}

/** A pack in one currency (`credit_eur` is in it too). */
export interface PackPrices {
  pack_id: number | null;
  name: string | null;
  public: boolean | null;
  active: boolean | null;
  price: Decimal;
  credit_eur: Decimal;
}

/** One key in every currency it is priced in; a currency absent from `prices` = not priced in it. */
export interface PriceTableOffer {
  key: string;
  name: string;
  prices: Record<string, OfferPrices>;
}

export interface PriceTablePack {
  key: string;
  name: string;
  prices: Record<string, PackPrices>;
}

export interface PriceTable {
  currencies: CurrencyOut[];
  offers: PriceTableOffer[];
  packs: PriceTablePack[];
}

export interface OfferPricesIn {
  key: string;
  price_per_seat: Decimal;
  allowance_per_seat_eur: Decimal | null;
  user_cap_eur: Decimal | null;
  credit_tiers: CreditTier[] | null;
}

export interface PackPricesIn {
  key: string;
  price: Decimal;
  credit_eur: Decimal;
  name?: string;
}

/** PUT /admin/price-table/{currency}: that currency's column; keys left out are untouched. A key missing in the
 *  currency is created from the euro one; all or nothing (409 `currency_not_supported`, `reference_missing`,
 *  `tiers_mismatch`, `duplicate_tier`). */
export interface PricesIn {
  offers: OfferPricesIn[];
  packs: PackPricesIn[];
}

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'ended' | 'canceled';

export interface SubscriptionBrief {
  offer_key: string;
  offer_name: string;
  version: number;
  status: SubscriptionStatus;
  seats: number;
  trial: boolean;
  price_per_seat: Decimal;
  credit_tier_price: Decimal | null;
  credit_tier_usage: Decimal | null;
  /** Tier price + seats × price per seat, excluding VAT. */
  monthly_total: Decimal;
  currency: string;
  current_period_end: string;
}

export interface ScheduledChange {
  offer_key: string;
  offer_name: string;
  version: number;
  seats: number;
  price_per_seat: Decimal;
  credit_tier_price: Decimal | null;
  effective_at: string;
}

export interface Subscription extends SubscriptionBrief {
  id: number;
  offer_version_id: number;
  started_at: string;
  current_period_start: string;
  cancel_at_period_end: boolean;
  allowance_per_seat_eur: Decimal | null;
  user_cap_eur: Decimal | null;
  min_seats: number;
  max_seats: number | null;
  pool: Decimal | null;
  /** A whole period's pool on the current terms; differs from `pool` when prorated. */
  pool_full: Decimal | null;
  /** When a change paid mid-period prorated this period's pool (PLAN-offers.md §7.2); null = a full period. */
  pool_prorated_since: string | null;
  pool_spent: Decimal;
  credits: Decimal;
  seats_used: number;
  scheduled: ScheduledChange | null;
  pending_order: Order | null;
  newer_version: boolean;
}

export interface BillingState {
  subscription: Subscription | null;
  /** Why the agent is closed on the subscription side (no_subscription, free_full, trial_ended, payment_overdue). */
  reason: string | null;
}

export type OrderStatus = 'pending' | 'paid' | 'canceled' | 'failed';
export type OrderKind = 'subscribe' | 'change' | 'seats' | 'renewal' | 'pack';

export interface Order {
  id: number;
  organization_id: number;
  kind: OrderKind;
  status: OrderStatus;
  currency: string;
  /** Excluding VAT. */
  amount: Decimal;
  vat_scheme: VatScheme | null;
  vat_rate: Decimal | null;
  vat_amount: Decimal | null;
  /** Including VAT; null while held for review or without a billing profile. */
  total: Decimal | null;
  /** When a held order became payable (its VAT number settled). */
  payable_at: string | null;
  lines: InvoiceLine[];
  subscription_id: number | null;
  invoice_id: number | null;
  /** `mollie` when a card paid it, `manual` otherwise. */
  provider: string;
  /** The latest card payment's Mollie status; null when none was started. */
  payment_status: PaymentStatus | null;
  /** Including VAT: what was refunded, refunds not failed (PLAN-payments.md M5). */
  refunded: Decimal;
  /** A payment of it was charged back by the cardholder's bank (M5). */
  charged_back: boolean;
  created_at: string;
  paid_at: string | null;
  canceled_at: string | null;
}

/** Mollie's payment statuses. */
export type PaymentStatus = 'open' | 'pending' | 'authorized' | 'paid' | 'failed' | 'canceled' | 'expired';

/** One card payment attempt of an order through Mollie (sculpt-api PLAN-payments.md M3). */
export interface PaymentAdmin {
  id: number;
  order_id: number;
  organization_id: number;
  provider: string;
  /** Mollie's id, tr_…. */
  provider_ref: string;
  /** `first`: paid by the customer on Mollie's page; `recurring`: a renewal charged on the saved card (M4). */
  sequence_type: 'first' | 'recurring' | string;
  status: PaymentStatus;
  currency: string;
  /** Including VAT: what the card is charged. */
  amount: Decimal;
  method: string | null;
  card_label: string | null;
  card_country: string | null;
  mandate_ref: string | null;
  failure_reason: string | null;
  /** A chargeback Mollie reported (M5): the amount the bank took back, and when it was first seen. */
  charged_back_amount: Decimal | null;
  charged_back_at: string | null;
  checkout_url: string | null;
  dashboard_url: string | null;
  expires_at: string | null;
  paid_at: string | null;
  synced_at: string | null;
  created_at: string;
}

export interface OrderAdmin extends Order {
  organization_name: string;
  payload: Record<string, unknown>;
  provider_ref: string | null;
}

export interface OrdersQuery {
  status?: string;
  organization_id?: number;
  page?: number;
  page_size?: number;
}

export interface CreditGrant {
  id: number;
  source: 'pack' | 'staff';
  /** The organization's: `amount_eur` and `remaining_eur` are in it (the names are historical). */
  currency: string;
  amount_eur: Decimal;
  remaining_eur: Decimal;
  expires_at: string | null;
  note: string;
  created_at: string;
}

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'void';

export interface InvoiceLine {
  description: string;
  quantity: Decimal | number;
  unit_price: Decimal | number;
}

export type InvoiceKind = 'invoice' | 'credit_note';
/** Where the worker stands with a Qonto invoice (sculpt-api PLAN-payments.md M2); null for a manual one. */
export type IssueState = 'pending' | 'issued' | 'failed';

export interface Invoice {
  id: number;
  organization_id: number | null; // null once its organization is deleted: invoices are kept 10 years
  number: string | null; // null until Qonto numbers it
  kind: InvoiceKind;
  credits_invoice_id: number | null; // a credit note: the invoice it cancels
  order_id: number | null;
  status: InvoiceStatus;
  currency: string;
  amount: Decimal; // excluding VAT
  vat_scheme: VatScheme | null;
  vat_rate: Decimal | null;
  vat_amount: Decimal | null;
  total: Decimal | null; // including VAT
  description: string;
  lines: InvoiceLine[];
  period_start: string | null;
  period_end: string | null;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  sent_at: string | null;
  pdf_name: string | null;
  has_pdf: boolean;
  external_ref: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvoiceAdmin extends Invoice {
  notes: string;
  organization_name: string;
  created_by: number | null;
  provider: 'manual' | 'qonto';
  provider_ref: string | null; // Qonto's id
  issue_state: IssueState | null;
  issue_step: string | null; // client | draft | finalize | paid | pdf | send | credit
  attempts: number;
  last_error: string | null;
  channel: 'mail' | 'einvoice' | null;
  einvoicing_status: string | null;
}

export interface InvoiceCreate {
  organization_id: number;
  amount: Decimal;
  currency: string;
  description: string;
  lines: InvoiceLine[];
  period_start: string | null;
  period_end: string | null;
  issued_at: string | null;
  due_at: string | null;
  external_ref: string | null;
  notes: string;
}

export type InvoicePatch = Partial<Omit<InvoiceCreate, 'organization_id'>>;

export interface InvoicesQuery {
  organization_id?: number;
  status?: string;
  page?: number;
  page_size?: number;
}

export type MailStatus = 'queued' | 'sent' | 'failed';

export interface Outbox {
  id: number;
  to_email: string;
  to_name: string | null;
  locale: string;
  template: string;
  subject: string;
  status: MailStatus;
  attempts: number;
  scheduled_at: string;
  sent_at: string | null;
  last_error: string | null;
  provider_message_id: string | null;
  attachment_name: string | null;
  created_at: string;
}

export interface OutboxDetail extends Outbox {
  params: Record<string, unknown>;
  text: string;
  html: string;
}

export interface MailQuery {
  status?: string;
  to?: string;
  template?: string;
  page?: number;
  page_size?: number;
}

export type WaitlistRole = 'modeller' | 'lender' | 'investor' | 'advisor' | 'other';

export interface WaitlistSignup {
  id: number;
  email: string;
  role: WaitlistRole;
  lang: string;
  created_at: string;
  updated_at: string;
}

export interface WaitlistQuery {
  role?: string;
  email?: string;
  page?: number;
  page_size?: number;
}

/** sculpt-api `contact/schemas.py`: a request sent through the website's contact form. */
/** The visitor's line of work (the API's `Role`, the waiting list's keys). */
export type ContactRole = WaitlistRole;
export type ContactStatus = 'new' | 'in_progress' | 'done' | 'spam';

export interface ContactRequest {
  id: number;
  name: string;
  email: string;
  role: ContactRole;
  company: string | null;
  message: string | null;
  lang: string;
  /** The site's path the form was sent from. */
  page: string | null;
  status: ContactStatus;
  note: string | null;
  /** The staff member who last moved it out of `new`; cleared when it goes back to `new`. */
  handled_by: number | null;
  handled_by_email: string | null;
  handled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactRequestsQuery {
  role?: string;
  status?: string;
  /** Looks in the name, address and company. */
  q?: string;
  page?: number;
  page_size?: number;
}

export interface ContactRequestPatch {
  status?: ContactStatus;
  note?: string;
}

export interface AuditEvent {
  id: number;
  actor_id: number | null;
  action: string;
  target_kind: string;
  target_id: string;
  data: Record<string, unknown>;
  created_at: string;
}

export interface AuditQuery {
  action?: string;
  target_kind?: string;
  target_id?: string;
  actor_id?: number;
  page?: number;
  page_size?: number;
}

export interface Bucket {
  period: string;
  value: number;
}

export interface Stats {
  users_total: number;
  users_verified: number;
  users_active: number;
  users_staff: number;
  organizations_total: number;
  organizations_team: number;
  organizations_personal: number;
  organizations_suspended: number;
  organizations_archived: number;
  signups_per_week: Bucket[];
  /** EUR at client price. */
  billed_per_month: Bucket[];
  /** USD paid to providers for the customers' calls. */
  cost_per_month: Bucket[];
  /** USD paid to providers for Peerception's own calls: the review and the staff sandboxes, billed to nobody. */
  internal_cost_per_month?: Bucket[];
  invoices_outstanding_count: number;
  invoices_outstanding_amount: number;
  invoices_draft_count: number;
  outbox_queued: number;
  outbox_failed: number;
}

// ---- conversation quality (sculpt-api `sculpt/admin/quality.py`) ----

export interface Count {
  key: string;
  count: number;
}

export interface DayPoint {
  day: string;
  conversations: number;
  flagged: number;
  thumbs_up: number;
  thumbs_down: number;
  reviewed_success: number;
  reviewed_failure: number;
}

export interface OrgQuality {
  organization_id: number | null;
  name: string;
  conversations: number;
  flagged: number;
  thumbs_up: number;
  thumbs_down: number;
  reviewed: number;
  failures: number;
  spend_usd: number;
}

export interface Insights {
  days: number;
  conversations: number;
  turns: number;
  flagged: number;
  ratings: number;
  thumbs_up: number;
  thumbs_down: number;
  /** up / (up + down); null without ratings */
  satisfaction: number | null;
  card_answered: number;
  card_dismissed: number;
  /** the thread card: answered / (answered + dismissed) */
  response_rate: number | null;
  reviewed: number;
  /** reviewed success / reviewed with a clear outcome */
  success_rate: number | null;
  outcomes: Count[];
  severities: Count[];
  categories: Count[];
  failure_reasons: Count[];
  /** how the reviewed conversations tried to turn the agent away (`Misuse`) */
  misuse: Count[];
  negative_tags: Count[];
  /** the users' reports made in the window (absent from an API older than the reports) */
  reports?: number;
  /** the reasons of the reports made in the window (`ReportReason`) */
  report_reasons?: Count[];
  /** issues open or investigating now, whatever the window and organization */
  issues_open?: number;
  /** issues opened in the window */
  issues_opened?: number;
  /** issues closed in the window */
  issues_closed?: number;
  signals: Count[];
  review_spend_usd: number;
  /** The staff sandboxes' calls over the window: Peerception's, billed to nobody. */
  sandbox_spend_usd?: number;
  per_day: DayPoint[];
  per_organization: OrgQuality[];
  insights: string[];
}

export interface InsightsQuery {
  days?: number;
  organization_id?: number;
}

export type Outcome = 'success' | 'partial' | 'failure' | 'abandoned' | 'unclear';
/** sculpt-api `review/schemas.MISUSES`: how a conversation tried to turn the agent away; not a failure of the agent */
export type Misuse = 'off_scope' | 'extraction_attempt' | 'injected_instruction';
export type Severity = 'none' | 'low' | 'medium' | 'high';

export interface ConversationRow {
  thread_id: string;
  title: string;
  user_id: number;
  user_email: string;
  organization_id: number | null;
  organization_name: string | null;
  created_at: string;
  updated_at: string;
  turns: number;
  /** the thread card's rating */
  rating: 'up' | 'down' | null;
  thumbs_down: number;
  signals: Count[];
  cost_usd: number;
  outcome: Outcome | null;
  severity: Severity | null;
  category: string | null;
  summary: string | null;
  eval_candidate: boolean;
  /** open issues linked to the conversation or to its evidence */
  issues_open?: number;
  /** Out of the review's sample and not in its guardrail: no title or summary, opened only for a support request. */
  restricted: boolean;
}

export interface ConversationsQuery {
  days?: number;
  organization_id?: number;
  user_id?: number;
  outcome?: string;
  severity?: string;
  category?: string;
  rating?: string;
  flagged?: boolean;
  eval_candidate?: boolean;
  /** ever reported */
  report?: 'any';
  /** the issues linked to the conversation or its evidence: `open` (one still open), `any`, `none` */
  issue?: 'open' | 'any' | 'none';
  q?: string;
  page?: number;
  page_size?: number;
}

/** The add-in's range snapshot (`ChangeJournal.ts`): the cells of `address` before a write. */
export interface RangeSnapshot {
  sheet: string;
  address: string;
  values: unknown[][];
  formulas: unknown[][];
  numberFormats?: string[][];
}

export interface TranscriptToolCall {
  tool_call_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  status: string;
  risk?: string;
  error?: string | null;
  hint?: string | null;
  changes?: { sheet: string; address: string; cells: number } | null;
  /** The whole result (admin transcript); `{truncated, chars, head}` past 40,000 characters. */
  result?: unknown;
  /** The cells before the write, from the undo journal, when the add-in filed one. */
  before?: RangeSnapshot[];
  undone?: boolean;
}

export interface PlanSummary {
  operation?: string;
  name?: string;
  calls?: number;
  markdown?: string;
  [key: string]: unknown;
}

export interface TranscriptMessage {
  role: 'user' | 'assistant';
  content: string;
  tool_calls: TranscriptToolCall[] | null;
  plan?: PlanSummary | null;
  turn_index: number;
  /** The model's reasoning, when it returned any (admin transcript). */
  thinking?: string[];
  model?: string | null;
  usage?: { input_tokens?: number; output_tokens?: number; [key: string]: unknown } | null;
}

export interface FeedbackEntry {
  turn_index: number | null;
  outcome: 'rated' | 'dismissed';
  rating: 'up' | 'down' | null;
  tags: string[];
  comment: string | null;
  source: string | null;
  revision: number;
  updated_at: string;
}

/** sculpt-api `feedback/schemas.REPORT_REASONS`, the add-in's report panel */
export type ReportReason = 'wrong_result' | 'damaged_workbook' | 'inappropriate' | 'data_concern' | 'other';

/** A user's report of the conversation to the team: evidence, with no status; it opened or joined an issue. */
export interface ConversationReportEntry {
  id: number;
  reason: ReportReason;
  comment: string | null;
  created_at: string;
  /** the issues it is linked to */
  issue_ids?: number[];
}

export interface ConversationEventEntry {
  id: number;
  issue_ids?: number[];
  turn_index: number | null;
  kind: string;
  data: Record<string, unknown>;
  created_at: string;
}

export interface ConversationReview {
  id: number;
  status: 'running' | 'done' | 'failed';
  selected: 'flagged' | 'sample' | null;
  flags: string[];
  turns: number | null;
  model: string | null;
  cost_usd: number | null;
  error: string | null;
  intent: string | null;
  category: string | null;
  outcome: Outcome | null;
  failure_reasons: string[];
  severity: Severity | null;
  sentiment: string | null;
  summary: string | null;
  worst_turn: number | null;
  eval_candidate: boolean;
  eval_note: string | null;
  product_insight: string | null;
  misuse: Misuse[];
  created_at: string;
  reviewed_at: string | null;
  issue_ids?: number[];
}

export interface ConversationDetail {
  conversation: ConversationRow;
  mode: string;
  workbook_id: string | null;
  index_version?: number | null;
  /** The reference accepted for it, when the conversation is in the replay corpus. */
  replay_golden?: ReplayGolden | null;
  messages: TranscriptMessage[];
  feedback: FeedbackEntry[];
  /** linked to the conversation or to any of its evidence */
  issues?: IssueRef[];
  /** linked to the conversation itself */
  issue_ids?: number[];
  /** newest first */
  reports?: ConversationReportEntry[];
  events: ConversationEventEntry[];
  reviews: ConversationReview[];
  cost_by_purpose: Record<string, number>;
  /** Each turn as it ran (PLAN-index-history.md D1); empty for a thread started before turns were recorded. */
  turns?: TurnRecord[];
}

/** A turn as it ran: the index version it read, whether that version is still stored, the code and the model. */
export interface TurnRecord {
  turn_index: number;
  index_version: number | null;
  stored: boolean;
  release: string | null;
  model: string | null;
  effort: string | null;
  mode: string | null;
}

/** The index a replay read for one turn: the catalog the turn read (`as_run`), one built again from that version's
 *  sheets by today's code (`rebuilt`), or none (`missing`: the version is no longer stored). */
export interface ReplayTurn {
  turn: number;
  index_version: number;
  catalog: 'as_run' | 'rebuilt' | 'missing';
}

/** POST /admin/conversations/{id}/replay: the conversation's server side played again on the code running now.
 *  One answer — does the production code still reproduce it? — and the differing calls for whoever fixes it. */
export type ReplayOutcome = 'no_deviation' | 'deviation' | 'nothing_to_compare' | 'not_replayable';

export interface ReplayDeviation {
  turn: number;
  tool: string;
  recorded: string;
  replayed: string;
  note: string;
}

/** The reference accepted for a conversation — the moment it became an eval — and the latest run against it. */
export interface ReplayGolden {
  thread_id: string;
  workbook_id: string;
  index_version: number;
  accepted_by: number | null;
  accepted_by_email: string | null;
  accepted_at: string;
  code_version: string | null;
  reason: string | null;
  compared: number;
  last_outcome: string | null;
  last_replayed_at: string | null;
  last_code_version: string | null;
  last_deviations: ReplayDeviation[] | null;
  title: string | null;
}

export interface ReplayOut {
  outcome: ReplayOutcome;
  summary: string;
  /** What it was measured against: the conversation's own recording, or the reference accepted for it. */
  against: 'recording' | 'golden';
  index_version: number | null;
  compared: number;
  deviations: ReplayDeviation[];
  golden: ReplayGolden | null;
  /** Each turn's index (PLAN-index-history.md D6). */
  turns?: ReplayTurn[];
}

/** GET /admin/credits (PLAN-offers.md §15.7): monthly credits against pack credits, current periods. */
export interface CreditsRow {
  organization_id: number;
  organization_name: string;
  offer_key: string | null;
  offer_name: string | null;
  status: SubscriptionStatus | null;
  /** The subscription's: `credit_tier_price` and `monthly_total` are in it; every credits field is in EUR (the ledger). */
  currency: string;
  credit_tier_price: Decimal | null;
  monthly_total: Decimal;
  period_start: string | null;
  period_end: string | null;
  /** The period's monthly credits; null = unlimited. */
  pool: Decimal | null;
  /** A whole period's pool on the current terms; differs from `pool` when prorated. */
  pool_full: Decimal | null;
  /** When a change paid mid-period prorated this period's pool (PLAN-offers.md §7.2); null = a full period. */
  pool_prorated_since: string | null;
  pool_spent: Decimal;
  pool_left: Decimal | null;
  /** Pack credits drawn during this period. */
  pack_spent: Decimal;
  pack_balance: Decimal;
  pack_next_expiry: string | null;
  pack_expired: Decimal;
}
export interface CreditsTotals {
  organizations: number;
  pool: Decimal;
  pool_spent: Decimal;
  pool_left: Decimal;
  pack_spent: Decimal;
  pack_balance: Decimal;
  pack_expired: Decimal;
  packs_sold: Decimal;
  packs_given: Decimal;
}
export interface CreditsOverview {
  totals: CreditsTotals;
  items: CreditsRow[];
}

// ---- VAT numbers VIES could not answer for, EU distance sales (sculpt-api PLAN-payments.md P9, §4) ----

export interface VatReview {
  organization_id: number;
  organization_name: string;
  customer_kind: CustomerKind | null;
  billing_name: string | null;
  billing_street: string | null;
  billing_zip: string | null;
  billing_city: string | null;
  billing_country: string | null;
  vat_number: string | null;
  vat_status: VatStatus;
  vat_checked_at: string | null;
  vat_check_name: string | null;
  vat_check_address: string | null;
  held_orders: number;
  /** Excluding VAT. */
  held_amount: Decimal;
  updated_at: string;
}

export interface VatDecision {
  vat_status: VatStatus;
  released_orders: number[];
}

/** Paid orders charged French VAT to customers elsewhere in the EU, against the €10,000 threshold (CGI art. 259 D). */
export interface EuDistanceSales {
  year: number;
  total: Decimal;
  previous_year_total: Decimal;
  warning: Decimal;
  threshold: Decimal;
}

/** What a refund does besides giving money back (PLAN-payments.md M5). */
export type RefundEffect = 'none' | 'cancel_at_period_end' | 'cancel_now' | 'remove_credits';

export interface RefundIn {
  /** Including VAT. */
  amount: Decimal;
  /** Printed on the credit note. */
  reason: string;
  effect: RefundEffect;
}

/** Money given back on a paid order: to the card through Mollie, or recorded for an order paid by transfer. */
export interface RefundAdmin {
  id: number;
  order_id: number;
  organization_id: number;
  payment_id: number | null;
  /** `mollie` (to the card) or `manual` (staff send it by transfer). */
  provider: 'mollie' | 'manual' | string;
  /** Mollie's id, re_…. */
  provider_ref: string | null;
  /** Mollie's (queued, pending, processing, refunded, failed, canceled), or `refunded` for a manual one. */
  status: string;
  currency: string;
  /** Including VAT: what was given back (can be a cent below the amount typed, to match the credit note). */
  amount: Decimal;
  reason: string;
  effect: RefundEffect;
  credit_note_id: number | null;
  created_by: number | null;
  created_at: string;
}

/** A month's money, for reconciling with Mollie's settlements and the bank (PLAN-payments.md §7). */
export interface MonthlyBilling {
  /** YYYY-MM. */
  month: string;
  /** The one currency the figures are in (`?currency=`, default EUR). */
  currency: string;
  /** Those the month had paid orders in, EUR always included. */
  currencies: string[];
  paid_orders: number;
  /** Excluding VAT. */
  amount: Decimal;
  vat: Decimal;
  /** Including VAT. */
  total: Decimal;
  by_card: Decimal;
  by_transfer: Decimal;
  refunds: number;
  refunded: Decimal;
  chargebacks: number;
  charged_back: Decimal;
}

// ---- issues (sculpt-api `admin/issues.py`, PLAN-issues.md) ----

export type IssueStatus = 'open' | 'investigating' | 'fixed' | 'wont_fix' | 'duplicate';
export type IssueSeverity = 'low' | 'medium' | 'high' | 'critical';
/** the evidence an issue links: `ref` is the thread id of a conversation, the row id of the others */
export type IssueLinkKind = 'conversation' | 'report' | 'review' | 'signal';
/** who made a change, or opened the issue */
export type IssueVia = 'staff' | 'report' | 'review';

export interface IssueRef {
  id: number;
  title: string;
  status: IssueStatus;
  severity: IssueSeverity;
}

export interface IssueLinkIn {
  kind: IssueLinkKind;
  ref: string;
}

export interface FixRef {
  label: string;
  url: string;
}

export interface IssueRow {
  id: number;
  title: string;
  status: IssueStatus;
  severity: IssueSeverity;
  labels: string[];
  /** who opened it: a staff member, a user's report, or the daily review */
  source: IssueVia;
  owner_id: number | null;
  owner_email: string | null;
  /** the links, per kind */
  links: Partial<Record<IssueLinkKind, number>>;
  /** distinct users and organizations of the linked conversations */
  users: number;
  organizations: number;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
}

/** One piece of evidence: what it points to, described; a report by its reason only (its comment is read on the
 *  conversation, an audited reading). */
export interface IssueEvidence {
  id: number;
  kind: IssueLinkKind;
  ref: string;
  thread_id: string;
  /** false once what it points to is gone */
  found: boolean;
  conversation_title: string | null;
  user_email: string | null;
  organization_name: string | null;
  at: string | null;
  reason?: ReportReason | null;
  outcome?: Outcome | null;
  severity?: Severity | null;
  category?: string | null;
  summary?: string | null;
  signal_kind?: string | null;
  turn_index?: number | null;
  added_by_email: string | null;
  created_at: string;
}

export interface IssueTimelineItem {
  type: 'comment' | 'event';
  id: number;
  actor_id: number | null;
  /** a staff member's; null for a report, the review or a deleted Account */
  actor_email: string | null;
  via: IssueVia;
  created_at: string;
  /** event: created, title, description, status, severity, owner, labels, fix_refs, linked, unlinked, comment_edited */
  kind?: string | null;
  /** `{from, to}` of a change; the review's `review_id`, `model` and `reason` */
  data?: Record<string, unknown>;
  body?: string | null;
  edited_at?: string | null;
}

export interface IssueDetail extends IssueRow {
  description: string | null;
  duplicate_of: IssueRef | null;
  fix_refs: FixRef[];
  created_by_email: string | null;
  evidence: IssueEvidence[];
  timeline: IssueTimelineItem[];
}

export interface IssuesQuery {
  status?: 'open' | 'closed' | 'all';
  severity?: IssueSeverity;
  owner_id?: number;
  label?: string;
  thread_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
}

export interface IssueCreate {
  title: string;
  description?: string | null;
  severity?: IssueSeverity;
  labels?: string[];
  owner_id?: number | null;
  links?: IssueLinkIn[];
}

export interface IssuePatch {
  title?: string;
  description?: string | null;
  status?: IssueStatus;
  duplicate_of?: number | null;
  severity?: IssueSeverity;
  labels?: string[];
  owner_id?: number | null;
  fix_refs?: FixRef[];
}

/** Staff sandboxes (sculpt-api PLAN-sandbox.md, sculpt/sandbox/SPEC.md): a conversation copied for staff to play again. */
export type SandboxState = 'idle' | 'running' | 'waiting' | 'failed';
export type AgentMode = 'plan' | 'ask' | 'auto' | 'full_auto';

export interface SandboxOpenIn {
  from_turn: number;
  model?: string | null;
  /** Optional: a sandbox opened from a message asks for none. */
  reason?: string;
  issue_id?: number | null;
  approve_all?: boolean;
}

/** What a turn stopped on: a question card (`ask_user`) or calls to confirm. */
export interface SandboxPending {
  reason: 'question' | 'confirmation';
  turn_index: number;
  recorded: number | null;
  batch: { batch_id: string; calls: { tool_call_id: string; tool_name: string; input: Record<string, unknown>; requires_confirmation?: boolean }[] };
}

export interface Sandbox {
  id: string;
  source_thread_id: string;
  source_title: string | null;
  source_workbook_id: string;
  source_version: number;
  from_turn: number;
  next_turn: number;
  staff_id: number | null;
  staff_email: string | null;
  thread_id: string;
  workbook_id: string;
  model: string;
  effort: string | null;
  reason: string;
  issue_id: number | null;
  approve_all: boolean;
  state: SandboxState;
  pending: SandboxPending | null;
  error: string | null;
  cost_usd: number;
  created_at: string;
  last_activity_at: string;
  expires_at: string;
}

/** A recorded user message the sandbox can send ("Play next"). */
export interface SandboxSourceTurn {
  turn_index: number;
  message: string;
  mode: string | null;
  index_version: number | null;
  documents: number;
  selection: boolean;
  /** How long the agent took on it for the user (the add-in's recorded turn time), when recorded. */
  duration_ms?: number | null;
}

export interface SandboxTurnSummary {
  tools: string[];
  plans: { name: string | null; status: string | null; calls: number | null; failed: number | null }[];
  text: string;
}

export interface SandboxComparison {
  sandbox_turn: number;
  recorded_turn: number;
  recorded: SandboxTurnSummary;
  sandbox: SandboxTurnSummary;
  same_tools: boolean;
  same_plans: boolean;
}

export interface SandboxUpdateIn {
  approve_all: boolean;
}

export interface SandboxDetail extends Sandbox {
  source_turns: SandboxSourceTurn[];
  messages: TranscriptMessage[];
  comparison: SandboxComparison[];
}

export interface SandboxEvent {
  id: number;
  turn_index: number;
  event: string;
  data: string;
  created_at: string;
}
